import { useState, useCallback } from "react";

interface UseVoiceToTextOptions {
  onTranscription?: (text: string, confidence: number) => void;
  onError?: (error: string) => void;
}

interface TranscriptionResult {
  text: string;
  confidence: number;
  language?: string;
}

type TranscribeResponse = {
  success?: boolean;
  text?: string;
  confidence?: number;
  language?: string;
  error?: {
    code?: string;
    message?: string;
  };
};

export function useVoiceToText(options: UseVoiceToTextOptions = {}) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastTranscription, setLastTranscription] = useState<TranscriptionResult | null>(null);

  const transcribeAudio = useCallback(
    async (audioData: Float32Array, sampleRate: number = 16000) => {
      setIsProcessing(true);

      try {
        // 音声データを配列に変換（JSONで送信するため）
        const audioArray = Array.from(audioData);

        const response = await fetch("/api/voice/transcribe", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            audioData: audioArray,
            sampleRate,
          }),
        });

        const result = (await response.json()) as TranscribeResponse;

        if (!response.ok || !result.success) {
          throw new Error(result.error?.message || "Transcription failed");
        }

        const transcriptionResult: TranscriptionResult = {
          text: result.text ?? "",
          confidence: result.confidence ?? 0,
          language: result.language,
        };

        setLastTranscription(transcriptionResult);
        options.onTranscription?.(transcriptionResult.text, transcriptionResult.confidence);

        return transcriptionResult;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        console.error("Transcription error:", error);
        options.onError?.(errorMessage);
        throw error;
      } finally {
        setIsProcessing(false);
      }
    },
    [options],
  );

  return {
    transcribeAudio,
    isProcessing,
    lastTranscription,
  };
}
